'use client';

import {
  IconCaretDownFilled,
  IconChevronLeft,
  IconMenu2,
} from '@tabler/icons-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import React, { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  cn,
  Sheet,
  SheetContent,
  SheetTrigger,
} from 'ui';
import { Icon } from '@tabler/icons-react';
import { PathSlugs, useEvaluatePathSlug } from '../hooks/useEvaluatePathSlug';

export interface SettingsNavbarItemLink {
  nestedRouting?: boolean;
  title: string;
  href: string;
  matchSegment: string;
}

export interface SettingsNavbarItem
  extends Omit<SettingsNavbarItemLink, 'href' | 'matchSegment'> {
  icon: Icon;
  items: SettingsNavbarItemLink[];
}

export const BackIcon = () => {
  return (
    <IconChevronLeft
      style={{
        marginRight: '0.375rem',
      }}
      className='size-4'
    />
  );
};

const SettingsNavbar = ({
  items,
  currentPath,
  onLinkClick,
}: {
  items: SettingsNavbarItem[];
  currentPath: string;
  onLinkClick?: () => void;
}) => {
  return (
    <Accordion defaultValue={items.map((item) => item.title)} type='multiple'>
      {items.map((item) => {
        const [currentLink] = item.items
          .filter((item) => currentPath.includes(item.matchSegment))
          .sort(
            (a, b) => (b.nestedRouting ? 1 : 0) - (a.nestedRouting ? 1 : 0),
          )!;

        return (
          <AccordionItem key={item.title} value={item.title} className='pb-0'>
            <AccordionTrigger
              className='justify-normal pb-0 hover:no-underline'
              withChevron={false}
            >
              <div className='flex flex-row items-center'>
                <item.icon className='mr-[0.4375rem] size-[0.938rem] text-stone-400' />
                <p className='text-xs font-medium text-foreground-secondary'>
                  {item.title}
                </p>
              </div>

              <IconCaretDownFilled
                className={cn(
                  'text-foreground-secondary ml-[0.75rem] h-[0.625rem] w-[0.625rem] shrink-0 text-muted-foreground transition-transform duration-200',
                )}
              />
            </AccordionTrigger>
            <AccordionContent className='pt-[0.5rem] pb-0'>
              <ul className='space-y-[0.25rem] ml-5'>
                {item.items.map((item, index) => {
                  const path = `/settings/${item.href}`;

                  return (
                    <li className='w-full h-[1.75rem]' key={index}>
                      <Link
                        onClick={onLinkClick}
                        className={cn(
                          'items-center px-[0.375rem] py-1 h-full transition-all flex rounded-[0.375rem] w-full hover:bg-sidebar-accent text-xs font-medium text-foreground',
                          {
                            'bg-sidebar-accent':
                              currentLink?.title === item.title,
                          },
                        )}
                        href={`${path}?q=${Math.floor(Math.random() * 100000)}`}
                      >
                        {item.title}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export const SettingsLayout = ({
  children,
  items,
  nextSlugs = [],
  backPath,
}: {
  children: React.ReactNode;
  items: SettingsNavbarItem[];
  nextSlugs?: PathSlugs;
  backPath?: string;
}) => {
  const currentPath = usePathname();
  const isNextSlug = useEvaluatePathSlug(nextSlugs);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className='w-full h-full min-h-screen py-2 flex bg-stone-100'>
      {/* Mobile Header */}
      <div className='md:hidden fixed top-0 left-0 right-0 z-50 bg-stone-100 border-b border-stone-200 px-4 py-3'>
        <div className='flex items-center justify-between'>
          <Link href={backPath || '/'}>
            <button className='flex flex-row items-center'>
              <BackIcon />
              <h2 className='text-[0.938rem] font-medium text-foreground'>
                Configuración
              </h2>
            </button>
          </Link>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant='ghost' size='sm' className='p-2'>
                <IconMenu2 className='size-5' />
              </Button>
            </SheetTrigger>
            <SheetContent side='left' className='bg-stone-100 w-64 p-4'>
              <div className='mt-4'>
                <SettingsNavbar
                  items={items}
                  currentPath={currentPath}
                  onLinkClick={() => setMobileMenuOpen(false)}
                />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className='flex flex-row px-4 md:px-6 md:space-x-6 flex-grow pt-14 md:pt-0'>
        {/* Desktop Sidebar */}
        <div className='hidden md:block flex-start items-start space-y-5 mt-[0.938rem] bg-stone-100'>
          <Link className='h-full pt-1 sticky top-6' href={backPath || '/'}>
            <button className='flex flex-row items-center'>
              <BackIcon />
              <h2 className='text-[0.938rem] font-medium text-foreground'>
                Configuración
              </h2>
            </button>
          </Link>

          <div className='w-[12.56rem] sticky top-20'>
            <SettingsNavbar items={items} currentPath={currentPath} />
          </div>
        </div>

        <div className='w-full'>
          <div className='bg-stone-50 border-stone-200 border rounded-[0.75rem] flex items-start justify-center p-6 h-full w-full'>
            <div
              className={cn('w-full px-4 md:px-0', {
                '': !isNextSlug,
              })}
            >
              {isNextSlug ? (
                <div className='flex flex-row items-start justify-center'>
                  <div className='w-full '>{children}</div>
                </div>
              ) : (
                children
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
